import * as Localization from "expo-localization";
import i18n, { use as registerPlugin } from "i18next";
import { initReactI18next } from "react-i18next";
import { Platform } from "react-native";

import { getAndroidApplicationLocales } from "../../modules/android-app-locales";
import cs from "./locales/cs.json";
import en from "./locales/en.json";

export const SUPPORTED_LANGUAGES = ["en", "cs"] as const;
export type AppLanguage =
    | (typeof SUPPORTED_LANGUAGES)[number]
    | "system"
    | "browser";

export function isAppLanguage(value: string): value is AppLanguage {
    return (SUPPORTED_LANGUAGES as readonly string[]).includes(value);
}

export function isSystemLanguage(value: string): value is "system" | "browser" {
    return value === "system" || value === "browser";
}

export const SYSTEM_LANGUAGE = Platform.OS === "web" ? "browser" : "system";

function toSupportedLanguage(
    languageCode: string | null | undefined,
): "cs" | "en" {
    return languageCode?.toLowerCase() === "cs" ? "cs" : "en";
}

/**
 * Language the OS wants this app to use: Android per-app language when set
 * (Settings → App language), otherwise the device language.
 *
 * expo-localization's getLocales() uses LocaleList.getDefault(), which on
 * Android 16 can ignore the per-app override and return the device language.
 */
export function detectDeviceLanguage(): "cs" | "en" {
    const applicationLocale = getAndroidApplicationLocales();
    if (applicationLocale) {
        return toSupportedLanguage(applicationLocale.split("-")[0]);
    }

    return toSupportedLanguage(
        Localization.getLocales()[0]?.languageCode ?? "en",
    );
}

/** BCP 47 tag for Intl date/number formatting. */
export function getFormattingLocale(language: string = i18n.language): string {
    return language.startsWith("cs") ? "cs-CZ" : "en-US";
}

void registerPlugin(initReactI18next).init({
    resources: {
        en: { translation: en },
        cs: { translation: cs },
    },
    lng: detectDeviceLanguage(),
    fallbackLng: "en",
    supportedLngs: [...SUPPORTED_LANGUAGES],
    interpolation: {
        escapeValue: false,
    },
    compatibilityJSON: "v4",
});

export default i18n;
